import { inject, Injectable } from "@angular/core";
import { ActivatedRouteSnapshot, DetachedRouteHandle, RouteReuseStrategy } from "@angular/router";

export type RealEstateRetainedRoute = "properties" | "sources";

export interface RealEstateScrollTarget {
  selector: string;
  top: number;
  left?: number;
}

interface RetainedScrollState {
  route: RealEstateRetainedRoute;
  targets: readonly RealEstateScrollTarget[];
}

@Injectable({ providedIn: "root" })
export class RealEstateRouteRetention {
  private pendingRoute: RealEstateRetainedRoute | null = null;
  private scrollState: RetainedScrollState | null = null;
  private restoreTimeout: number | null = null;

  public retainOnNextNavigation(route: RealEstateRetainedRoute, targets: readonly RealEstateScrollTarget[]): void {
    this.pendingRoute = route;
    this.scrollState = { route, targets };
  }

  public consumeRetention(route: RealEstateRetainedRoute): boolean {
    if (this.pendingRoute !== route) return false;
    this.pendingRoute = null;
    return true;
  }

  public restoreScrollAfterAttach(route: RealEstateRetainedRoute): void {
    const state = this.scrollState;
    if (!state || state.route !== route) return;
    if (this.restoreTimeout !== null) window.clearTimeout(this.restoreTimeout);

    this.restoreTimeout = window.setTimeout(() => {
      for (const target of state.targets) {
        const element = document.querySelector<HTMLElement>(target.selector);
        if (!element) continue;
        element.scrollTop = target.top;
        element.scrollLeft = target.left ?? 0;
      }
      this.restoreTimeout = null;
    }, 0);
  }

  public clearRetainedState(route: RealEstateRetainedRoute): void {
    if (this.pendingRoute === route) this.pendingRoute = null;
    if (this.scrollState?.route === route) this.scrollState = null;
    if (this.restoreTimeout !== null) window.clearTimeout(this.restoreTimeout);
    this.restoreTimeout = null;
  }
}

@Injectable()
export class RealEstateRouteReuseStrategy implements RouteReuseStrategy {
  private readonly retention = inject(RealEstateRouteRetention);
  private readonly handles = new Map<RealEstateRetainedRoute, DetachedRouteHandle>();

  public shouldDetach(route: ActivatedRouteSnapshot): boolean {
    const key = this.routeKey(route);
    if (!key) return false;

    const shouldRetain = this.retention.consumeRetention(key);
    if (!shouldRetain) {
      this.handles.delete(key);
      this.retention.clearRetainedState(key);
    }
    return shouldRetain;
  }

  public store(route: ActivatedRouteSnapshot, handle: DetachedRouteHandle | null): void {
    const key = this.routeKey(route);
    if (!key) return;
    if (handle) this.handles.set(key, handle);
    else this.handles.delete(key);
  }

  public shouldAttach(route: ActivatedRouteSnapshot): boolean {
    const key = this.routeKey(route);
    return Boolean(key && this.handles.has(key));
  }

  public retrieve(route: ActivatedRouteSnapshot): DetachedRouteHandle | null {
    const key = this.routeKey(route);
    if (!key) return null;
    this.retention.restoreScrollAfterAttach(key);
    return this.handles.get(key) ?? null;
  }

  public shouldReuseRoute(future: ActivatedRouteSnapshot, current: ActivatedRouteSnapshot): boolean {
    return future.routeConfig === current.routeConfig;
  }

  private routeKey(route: ActivatedRouteSnapshot): RealEstateRetainedRoute | null {
    const value: unknown = route.routeConfig?.data?.["retainRealEstateView"];
    return value === "properties" || value === "sources" ? value : null;
  }
}
